import { computed, onBeforeUnmount, onMounted, ref, toValue } from 'vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { MESSAGE_TYPE } from 'shared/constants/messages';

const WHATSAPP_SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const TICK_INTERVAL_MS = 60 * 1000;

function isWhatsappInbox(inbox) {
  if (!inbox) return false;
  if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return true;
  return (
    inbox.channel_type === INBOX_TYPES.TWILIO && inbox.medium === 'whatsapp'
  );
}

function findLastIncomingAtMs(conversation) {
  const messages = conversation?.messages || [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.message_type === MESSAGE_TYPE.INCOMING && message.created_at) {
      return message.created_at * 1000;
    }
  }
  return null;
}

export function useWhatsappSessionCountdown({ conversation, inbox }) {
  const now = ref(Date.now());
  let intervalId = null;

  onMounted(() => {
    now.value = Date.now();
    intervalId = window.setInterval(() => {
      now.value = Date.now();
    }, TICK_INTERVAL_MS);
  });

  onBeforeUnmount(() => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  });

  const remainingMs = computed(() => {
    const chat = toValue(conversation);
    const box = toValue(inbox);
    if (!chat || !box) return null;
    if (!isWhatsappInbox(box)) return null;
    if (!chat.can_reply) return null;

    const lastIncomingAt = findLastIncomingAtMs(chat);
    if (!lastIncomingAt) return null;

    const diff = lastIncomingAt + WHATSAPP_SESSION_WINDOW_MS - now.value;
    return diff > 0 ? diff : null;
  });

  const isVisible = computed(() => remainingMs.value !== null);

  const severity = computed(() => {
    const ms = remainingMs.value;
    if (ms === null) return 'neutral';
    if (ms < FIFTEEN_MINUTES_MS) return 'critical';
    if (ms < ONE_HOUR_MS) return 'warning';
    return 'neutral';
  });

  const timeParts = computed(() => {
    const ms = remainingMs.value;
    if (ms === null) return null;
    const totalMinutes = Math.max(1, Math.ceil(ms / (60 * 1000)));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes, showHours: ms >= ONE_HOUR_MS };
  });

  return { isVisible, severity, timeParts };
}
